import { ObjectTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { PropertyUpdatesByIdWithMetadata, savePropertyValues } from './api';
import {
  trackAutosaveSuccess,
  trackAutosaveUndo,
} from '../usage/usageTrackingUtils';
import {
  incrementAutosaveFailureMetric,
  incrementAutosaveOnSaveFailureMetric,
  incrementAutosaveSuccessMetric,
  incrementAutosaveUndoCallbackFailureMetric,
  incrementAutosaveUndoFailureMetric,
  incrementAutosaveUndoSuccessMetric,
} from '../metrics/autosaveMetrics';
import Raven from 'raven-js';
import { PromiseClient } from 'hub-http/adapters/promiseClient';
import Value from 'customer-data-objects/property/Value';
import { ObjectId } from 'customer-data-objects/objects/ObjectId';
import { AutosaveItem } from './types';

export type PropertyUpdates = {
  properties: Array<{ name: string; value: Value }>;
  previousProperties: Array<{ name: string; value: Value }>;
};

export type PropertyBatchUpdates = Record<ObjectId, Record<string, Value>>;

type PropertyBatch = {
  objectTypeId: ObjectTypeId;
  updates: PropertyBatchUpdates;
  previousUpdates: PropertyBatchUpdates;
  successCallbacks: Array<(result: PropertyUpdatesByIdWithMetadata) => void>;
  undoCallbacks: Array<(result: PropertyUpdatesByIdWithMetadata) => void>;
  failureCallbacks: Array<(error: Error) => void>;
};

type OnObjectsUpdated = (
  result: PropertyUpdatesByIdWithMetadata,
  objectTypeId: ObjectTypeId
) => void;

function getIdentifiers(batch: PropertyBatch) {
  return [
    ...new Set(
      Object.values(batch.updates).flatMap((update) => Object.keys(update))
    ),
  ];
}

function createBatches(items: AutosaveItem[]): PropertyBatch[] {
  const batches: Record<ObjectTypeId, PropertyBatch> = {};

  for (const item of items) {
    const { request, onSuccess, onFailure, onUndo } = item.data;
    const { objectTypeId, objectId, updates } = request;
    const { properties, previousProperties } = updates;

    if (!batches[objectTypeId]) {
      batches[objectTypeId] = {
        objectTypeId,
        updates: {},
        previousUpdates: {},
        successCallbacks: [],
        failureCallbacks: [],
        undoCallbacks: [],
      };
    }

    if (!batches[objectTypeId].updates[objectId]) {
      batches[objectTypeId].updates[objectId] = {};
    }
    if (!batches[objectTypeId].previousUpdates[objectId]) {
      batches[objectTypeId].previousUpdates[objectId] = {};
    }

    properties.forEach(({ name, value }) => {
      batches[objectTypeId].updates[objectId][name] = value;
    });
    previousProperties.forEach(({ name, value }) => {
      batches[objectTypeId].previousUpdates[objectId][name] = value;
    });
    batches[objectTypeId].successCallbacks.push(onSuccess);
    batches[objectTypeId].failureCallbacks.push(onFailure);
    batches[objectTypeId].undoCallbacks.push(onUndo);
  }

  return Object.values(batches);
}

export const savePropertyItems = async (
  items: AutosaveItem[],
  httpClient: PromiseClient,
  onObjectsUpdated?: OnObjectsUpdated
): Promise<boolean> => {
  const batches = createBatches(items);

  const allResults = await Promise.allSettled(
    batches.map(async (batch) => {
      const { objectTypeId, updates } = batch;
      const identifiers = getIdentifiers(batch);

      const result = await savePropertyValues(
        objectTypeId,
        updates,
        httpClient
      ).catch((error: Error) => {
        incrementAutosaveFailureMetric(error);
        batch.failureCallbacks.forEach((callback) => callback(error));
      });

      try {
        if (result) {
          trackAutosaveSuccess({
            identifiers,
            objectTypeId,
          });
          incrementAutosaveSuccessMetric(identifiers.length);
          batch.successCallbacks.forEach((callback) => callback(result));
          onObjectsUpdated?.(result, objectTypeId);
        }
      } catch (e) {
        incrementAutosaveOnSaveFailureMetric();
        Raven.captureException(new Error('Autosave property onsave failure'), {
          extra: {
            objectTypeId,
            identifiers,
            originalError: e,
          },
        });
      }

      return result;
    })
  );
  return allResults.every(
    (result) => result.status === 'fulfilled' && result.value
  );
};

export const undoPropertyItems = async (
  items: AutosaveItem[],
  httpClient: PromiseClient,
  onObjectsUpdated?: OnObjectsUpdated
): Promise<boolean> => {
  const batches = createBatches(items);

  const allResults = await Promise.allSettled(
    batches.map(async (batch) => {
      const { objectTypeId, previousUpdates, updates, failureCallbacks } =
        batch;

      const identifiers = getIdentifiers({
        ...batch,
        previousUpdates: updates,
        updates: previousUpdates,
      });

      trackAutosaveUndo({
        identifiers,
        objectTypeId,
      });

      const result = await savePropertyValues(
        objectTypeId,
        previousUpdates,
        httpClient
      ).catch((error: Error) => {
        failureCallbacks.forEach((callback) => callback(error));
        incrementAutosaveUndoFailureMetric();
      });

      try {
        if (result) {
          incrementAutosaveUndoSuccessMetric();
          onObjectsUpdated?.(result, objectTypeId);
          batch.undoCallbacks.forEach((callback) => callback(result));
        }
      } catch (e) {
        incrementAutosaveUndoCallbackFailureMetric();
        Raven.captureException(new Error('Autosave undo handler failed'), {
          extra: {
            objectTypeId,
            identifiers,
            originalError: e,
          },
        });
      }

      return result;
    })
  );
  return allResults.every(
    (result) => result.status === 'fulfilled' && result.value
  );
};
